import { Dispatch } from 'react';
import {
  Row, Col,
  Label,
  Button,
  Details,
} from 'nhsuk-react-components';
import CustomReactSelect from 'components/CustomReactSelect/CustomReactSelect';
import { RuleEditor } from '../../../../../schemas/IterationRule.schema';
import { RuleActions } from '../../../../../reducers/rule/ruleReducer';
import {
  getRuleEditorControl,
  updateRuleComparator,
} from '../../../../../utils/ruleEditorUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';

export interface PickListEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  errors: Record<string, string>;
}

export function PickListEditor({
  ruleEditor,
  dispatch,
  value,
  errors,
}: PickListEditorProps) {
  const pickerControl = getRuleEditorControl(ruleEditor, 'PICK_LIST');

  const options = (pickerControl?.values ?? []).map(({ Label: labelText, Value }: { Label: string; Value: string }) => ({
    label: `${labelText} (${Value})`,
    value: Value,
  }));

  const currentValues = value.split(',').filter(Boolean);
  const selectedOptions = currentValues.map((val) => options.find((o) => o.value === val) ?? { label: val, value: val });

  const defaultValues = (pickerControl?.defaultValue ?? '').split(',').filter(Boolean);

  const defaultDescriptions = defaultValues
    .map((val) => {
      const option = options.find((o) => o.value === val.trim());
      return option ? option.label : val;
    });

  const handleSelectionChange = (selected: { label: string; value: string }[] | null) => {
    const selectedValues = selected?.map((opt) => opt.value).filter(Boolean) ?? [];
    updateRuleComparator(dispatch, ruleEditor, selectedValues.join(','), selectedValues);
  };

  const handleAddDefaults = () => {
    const currentSelected = selectedOptions.filter((opt) => opt.value);
    const defaultOptions = defaultValues
      .map((val) => options.find((o) => o.value === val.trim()))
      .filter(Boolean) as { label: string; value: string }[];

    const merged = [...currentSelected];
    defaultOptions.forEach((defOpt) => {
      if (!merged.some((opt) => opt.value === defOpt.value)) {
        merged.push(defOpt);
      }
    });

    merged.sort((a, b) => a.label.localeCompare(b.label));

    if (merged.length > 0) {
      handleSelectionChange(merged);
    }
  };

  return (
    <>
      {defaultValues.length > 0 && (
        <Row>
          <Col width="full">
            <Details>
              <Details.Summary>
                Most common:
              </Details.Summary>
              <Details.Text>
                <ul>
                  {defaultDescriptions.map((desc, index) => (
                    <li key={defaultValues[index]}>{desc}</li>
                  ))}
                </ul>
              </Details.Text>
            </Details>
            <Button
              secondary
              onClick={handleAddDefaults}
              className="nhsuk-u-margin-top-2"
            >
              Add most common
            </Button>
          </Col>
        </Row>
      )}
      <Row>
        <Col width="full">
          <Label htmlFor="rule-comparator">
            {pickerControl?.label}
            :
          </Label>
          <CustomReactSelect
            options={options}
            inputId="rule-comparator"
            isMulti
            placeholder={pickerControl?.placeholderText || 'Select...'}
            onChange={handleSelectionChange}
            value={selectedOptions}
            isSearchable
          />
          <p className="nhsuk-hint nhsuk-u-margin-top-3">
            {selectedOptions.length}
            {' item'}
            {selectedOptions.length === 1 ? '' : 's'}
            {' selected from '}
            {options.length}
            {' '}
            {pickerControl?.label}
          </p>
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </>
  );
}

export default PickListEditor;
